import CustomOTPInput from '@components/shared/CustomOTPInput/CustomOTPInput';
import CustomText from '@components/shared/CustomText';
import StepLayout from '@features/DocViewer/Steps/StepLayout';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import useDetectEnterKey from '@shared/hooks/useDetectEnterKey';
import { DOC_VIEWER_STEPS } from '@shared/constants';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDocViewerContext } from 'context/DocViewerContext';
import { useSignaturesPaginationContext } from 'context/SignaturesPaginationContext';
import useVerifyOtp from '@hooks/useVerifyOtp';
import useLogin from '@hooks/useLogin';
import useSendOtp from '@hooks/useSendOtp';
import { getUserData, setUserData } from '@localStorage/userData';
import styles from './VerifySignee.module.scss';

const VerifySignee = ({ currentStep, setCurrentStep }) => {
  const [otp, setOtp] = useState('');
  const [isTimedOut, setIsTimedOut] = useState(true);
  const [counter, setCounter] = useState(60);
  const [error, setError] = useState('');

  const { getValues } = useFormContext();
  const { setPage } = useSignaturesPaginationContext();
  const { isPressed } = useDetectEnterKey();
  const { setUserID, setUserPhoneNumber } = useDocViewerContext();

  const { mutateAsync: verifyOtp, isLoading: isVerifyingOtp } =
    useVerifyOtp({}, true);

  const { mutateAsync: login } = useLogin({}, true);

  const { mutateAsync: sendOtp } = useSendOtp({}, true);

  const handleOnChange = value => setOtp(value);

  const handleSubmit = useCallback(async () => {
    try {
      let res = await verifyOtp({ otp, phone: getValues('phone') });

      try {
        res = await login({ tokens: [res.token] });

        setUserData({
          ...getUserData(),
          user_id: res.user_id,
          jwt_doc_viewer: res.token,
        });
        setPage(1);
        setUserID(res.user_id);
        setUserPhoneNumber(getValues('phone'));
        setCurrentStep(DOC_VIEWER_STEPS.docs);
        setError();
      } catch (err) {
        if (err.response?.data?.status === 425) {
          setIsTimedOut(true);
        } else {
          setError(`There are no docs for the number ${getValues('phone')}`);
        }
      }
    } catch (err) {
      setError('This OTP code is incorrect, or has expired.');
    }
  }, [verifyOtp, otp, getValues]);

  const handleResend = async () => {
    setIsTimedOut(true);
    try {
      await sendOtp({ phone: getValues('phone') });
      setError();
    } catch (err) {
      setIsTimedOut(true);
      setError('Unable to request OTP, please try again later.');
    }
  };

  useEffect(() => {
    let timer;
    if (isTimedOut) {
      timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    }
    if (counter === 0) {
      setIsTimedOut(false);
      setCounter(60);
    }
    return () => clearInterval(timer);
  }, [isTimedOut, counter]);

  useEffect(() => {
    if (isPressed && otp.length === 4 && !isVerifyingOtp) handleSubmit();
  }, [handleSubmit, isPressed, otp, isVerifyingOtp]);

  return (
    <StepLayout
      title={currentStep.title}
      primaryButtonOnClick={handleSubmit}
      primaryButtonText={'Next'}
      primaryButtonDisabled={
        otp.length < 4 || otp.includes(' ') || isVerifyingOtp
      }
      secondaryButtonText='Cancel'
    >
      <div className={styles.content}>
        <CustomOTPInput
          value={otp}
          onChange={handleOnChange}
          valueLength={4}
          error={error}
        />
        <CustomText
          onClick={handleResend}
          customStyle={clsx(styles.resend, {
            [styles.disabled]: isTimedOut,
          })}
        >
          {isTimedOut ? `Resend OTP in ${counter}s` : 'Resend OTP'}
        </CustomText>
      </div>
    </StepLayout>
  );
};

VerifySignee.propTypes = {
  currentStep: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.number,
    step: PropTypes.number,
  }),
  setCurrentStep: PropTypes.func,
};

export default VerifySignee;
